"use client";

import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { VolumeX } from "lucide-react";

import HlsVideoPlayer, {
  type HlsVideoPlayerActions,
} from "@/components/common/HlsVideoPlayer";
import {
  trackVideoCompleted,
  trackVideoProgress,
  trackVideoStarted,
} from "@/lib/analytics/track-client";
import type {
  BaseVideoProps,
  VideoProgressProps,
} from "@/lib/analytics/types";

const MILESTONES: VideoProgressProps["progress_pct"][] = [25, 50, 75, 95];

export interface VslHlsPlayerHandle {
  replay: () => void;
}

interface VslHlsPlayerProps {
  src: string;
  videoProps: BaseVideoProps;
  poster?: string;
  className?: string;
  /** Start muted playback on load and show a tap-to-unmute overlay */
  autoplayMuted?: boolean;
  onProgressPct?: (pct: number) => void;
  onEnded?: () => void;
  children?: ReactNode;
}

export const VslHlsPlayer = forwardRef<VslHlsPlayerHandle, VslHlsPlayerProps>(
  function VslHlsPlayer(
    {
      src,
      videoProps,
      poster,
      className,
      autoplayMuted,
      onProgressPct,
      onEnded,
      children,
    },
    ref,
  ) {
    const actionsRef = useRef<HlsVideoPlayerActions | null>(null);
    const trackedMilestones = useRef(
      new Set<VideoProgressProps["progress_pct"]>(),
    );
    const hasTrackedStart = useRef(false);
    const hasTrackedCompletion = useRef(false);
    const isMutedAutoplay = useRef(Boolean(autoplayMuted));
    const [showUnmute, setShowUnmute] = useState(false);

    useImperativeHandle(ref, () => ({
      replay: () => actionsRef.current?.replay(),
    }));

    const trackStartOnce = () => {
      if (hasTrackedStart.current) return;
      hasTrackedStart.current = true;
      trackVideoStarted({ ...videoProps, muted: isMutedAutoplay.current });
    };

    // Muted autoplay is not a start: the visitor did nothing yet. It becomes
    // one at the first progress milestone (they stayed and watched with the
    // burned-in captions) or on the unmute tap, whichever comes first.
    const handlePlay = () => {
      if (isMutedAutoplay.current) {
        setShowUnmute(true);
        return;
      }
      trackStartOnce();
    };

    const handleAutoplayBlocked = () => {
      isMutedAutoplay.current = false;
      setShowUnmute(false);
    };

    const handleUnmute = () => {
      isMutedAutoplay.current = false;
      setShowUnmute(false);
      trackedMilestones.current.clear();
      onProgressPct?.(0);
      trackStartOnce();
      actionsRef.current?.unmuteAndRestart();
    };

    const handleEnded = () => {
      if (!hasTrackedCompletion.current) {
        hasTrackedCompletion.current = true;
        trackVideoCompleted({ ...videoProps, muted: isMutedAutoplay.current });
      }
      onEnded?.();
    };

    const handleTimeUpdate = (currentTime: number, duration: number) => {
      const progressPct = (currentTime / duration) * 100;
      onProgressPct?.(progressPct);

      for (const milestone of MILESTONES) {
        if (
          progressPct >= milestone &&
          !trackedMilestones.current.has(milestone)
        ) {
          trackedMilestones.current.add(milestone);
          trackStartOnce();
          trackVideoProgress({
            ...videoProps,
            progress_pct: milestone,
            muted: isMutedAutoplay.current,
          });
        }
      }
    };

    return (
      <HlsVideoPlayer
        src={src}
        poster={poster}
        className={className}
        onPlay={handlePlay}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        actionsRef={actionsRef}
        autoPlayMuted={autoplayMuted}
        onAutoplayBlocked={handleAutoplayBlocked}
        noHotkeys
        autoHide={-1}
      >
        {showUnmute && (
          <button
            type="button"
            onClick={handleUnmute}
            aria-label="Toca para escuchar"
            className="absolute inset-0 z-20 flex cursor-pointer items-start justify-center pt-3 focus-visible:outline-none"
          >
            <span className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
              <VolumeX className="h-4 w-4" />
              Toca para escuchar
            </span>
          </button>
        )}
        {children}
      </HlsVideoPlayer>
    );
  },
);
